import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, Check, Mail, Download } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { USER_TYPES, SENTIMENTS } from "@/lib/options";

const initial = {
  user_type: "",
  story: "",
  city: "",
  sentiment: "",
  contact_email: "",
  contact_name: "",
  contact_phone: "",
  contact_permission: false,
  report_opt_in: false,
  consent: false,
};

export default function IntakeForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [publicId, setPublicId] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.consent) {
      toast.error("Please agree to the research consent to submit.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim())) {
      toast.error("Please enter a valid email address so we can send your Submission ID.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/submissions", {
        user_type: form.user_type,
        story: form.story,
        city: form.city,
        sentiment: form.sentiment,
        contact_email: form.contact_email.trim(),
        contact_name: form.contact_name,
        contact_phone: form.contact_phone,
        contact_permission: form.contact_permission,
        report_opt_in: form.report_opt_in,
        consent: form.consent,
      });
      setPublicId(res.data?.public_id || "");
      setSubmittedEmail(res.data?.email || form.contact_email.trim());
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const certUrl = publicId && submittedEmail
      ? `${API}/submissions/${encodeURIComponent(publicId)}/certificate?email=${encodeURIComponent(submittedEmail)}`
      : "";
    return (
      <div
        data-testid="submission-success"
        className="border border-slate-200 bg-white p-10 text-center md:p-16"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center border border-cardinal">
          <Check className="h-7 w-7 text-cardinal" />
        </div>
        <h3 className="mt-6 font-serif text-3xl font-medium tracking-tight text-ink">
          Thank you for contributing.
        </h3>

        {publicId && (
          <div
            data-testid="submission-id"
            className="mx-auto mt-6 inline-block border border-slate-200 bg-slate-50 px-6 py-4"
          >
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Submission ID</div>
            <div className="mt-1 font-mono text-lg text-navy">{publicId}</div>
          </div>
        )}

        {submittedEmail && (
          <p className="mx-auto mt-5 flex max-w-xl items-center justify-center gap-2 text-sm text-slate-600">
            <Mail className="h-4 w-4 text-calblue" />
            A confirmation email has been sent to{" "}
            <span className="font-medium text-ink">{submittedEmail}</span>
          </p>
        )}

        <p className="mx-auto mt-4 max-w-xl leading-relaxed text-slate-600">
          You can check your submission status anytime using your Submission ID and email
          address. As a research participant, your individual response stays private and is
          never published — it contributes only to anonymous, aggregate research.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {certUrl && (
            <a
              href={certUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="download-certificate-button"
              className="inline-flex items-center rounded-none bg-navy px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-navy-hover"
            >
              <Download className="mr-2 h-4 w-4" /> Download Contribution Certificate
            </a>
          )}
          <Link
            to="/status"
            data-testid="check-status-link"
            className="inline-flex items-center rounded-none border border-navy px-6 py-3 text-sm font-medium text-navy transition-colors hover:bg-navy hover:text-white"
          >
            Check Submission Status
          </Link>
        </div>

        <div className="mt-8">
          <Button
            data-testid="submit-another-button"
            variant="ghost"
            className="rounded-none text-slate-500 hover:text-ink"
            onClick={() => {
              setForm(initial);
              setStep(1);
              setDone(false);
              setPublicId("");
              setSubmittedEmail("");
            }}
          >
            Share another experience
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="intake-form"
      className="border border-slate-200 bg-white p-6 md:p-12"
    >
      {/* progress */}
      <div className="mb-10 flex items-center gap-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <span
              className={`flex h-7 w-7 flex-none items-center justify-center font-mono text-xs ${
                s <= step ? "bg-cardinal text-white" : "bg-slate-100 text-slate-400"
              }`}
            >
              {s}
            </span>
            {s < 3 && (
              <span
                className={`h-px flex-1 ${s < step ? "bg-cardinal" : "bg-slate-200"}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="crep-fade-up">
          <h3 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
            Who are you?
          </h3>
          <p className="mt-2 text-slate-500">
            Every perspective is welcome here.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {USER_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`who-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => set("user_type", t)}
                className={`flex items-center gap-3 border px-5 py-4 text-left text-sm transition-colors ${
                  form.user_type === t
                    ? "border-cardinal bg-cardinal-light text-ink"
                    : "border-slate-200 text-slate-700 hover:border-slate-400"
                }`}
              >
                <span
                  className={`h-3.5 w-3.5 flex-none rounded-full border ${
                    form.user_type === t ? "border-4 border-cardinal" : "border-slate-300"
                  }`}
                />
                {t}
              </button>
            ))}
          </div>
          <div className="mt-10 flex justify-end">
            <Button
              data-testid="step1-continue"
              disabled={!form.user_type}
              onClick={() => setStep(2)}
              className="rounded-none bg-cardinal px-8 py-6 text-base font-medium text-white hover:bg-cardinal-hover"
            >
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="crep-fade-up">
          <h3 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
            Tell us your story.
          </h3>
          <p className="mt-2 text-slate-500">
            In your own words. There is no length limit, and no wrong answer.
          </p>
          <Textarea
            data-testid="story-textarea"
            value={form.story}
            onChange={(e) => set("story", e.target.value)}
            rows={12}
            autoFocus
            placeholder="Take your time…"
            className="mt-8 rounded-none border-slate-200 bg-white text-base leading-relaxed focus-visible:ring-cardinal"
          />
          <div className="mt-10 flex items-center justify-between">
            <Button
              data-testid="step2-back"
              variant="ghost"
              onClick={() => setStep(1)}
              className="rounded-none text-slate-500 hover:text-ink"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button
              data-testid="step2-continue"
              disabled={!form.story.trim()}
              onClick={() => setStep(3)}
              className="rounded-none bg-cardinal px-8 py-6 text-base font-medium text-white hover:bg-cardinal-hover"
            >
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div className="crep-fade-up">
          <h3 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
            Thank you.
          </h3>
          <p className="mt-3 leading-relaxed text-slate-600">
            To help us better understand your experience, may we ask a few optional
            questions?
          </p>

          <div className="mt-8 space-y-8">
            {/* city */}
            <div>
              <label className="text-sm font-medium text-ink">
                What city or county does your experience involve?
                <span className="ml-2 text-xs font-normal text-slate-400">Optional</span>
              </label>
              <Input
                data-testid="city-input"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="e.g., Orange County, Sacramento"
                className="mt-3 rounded-none border-slate-200 bg-white py-6 focus-visible:ring-cardinal"
              />
            </div>

            {/* sentiment */}
            <div>
              <label className="text-sm font-medium text-ink">
                Overall, how would you describe the experience?
                <span className="ml-2 text-xs font-normal text-slate-400">Optional</span>
              </label>
              <div className="mt-3 flex flex-wrap gap-3">
                {SENTIMENTS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`sentiment-${s.toLowerCase()}`}
                    onClick={() => set("sentiment", form.sentiment === s ? "" : s)}
                    className={`border px-6 py-3 text-sm transition-colors ${
                      form.sentiment === s
                        ? "border-cardinal bg-cardinal-light text-ink"
                        : "border-slate-200 text-slate-700 hover:border-slate-400"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* email + contact */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-ink">
                  Email address
                  <span className="ml-2 text-xs font-normal text-cardinal">Required</span>
                </label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set("contact_email", e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 rounded-none border-slate-200 bg-white py-6 focus-visible:ring-cardinal"
                />
                <p className="mt-2 text-xs leading-relaxed text-slate-500">
                  Your email is used only to send your confirmation and unique Submission ID,
                  to let you check your submission status, and — if you opt in below — to
                  notify you when the annual report is published. It will never be published
                  or sold.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-500">
                    Name <span className="text-slate-400">(optional)</span>
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    value={form.contact_name}
                    onChange={(e) => set("contact_name", e.target.value)}
                    placeholder="Name"
                    className="mt-1 rounded-none border-slate-200 bg-white py-6 focus-visible:ring-cardinal"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">
                    Phone <span className="text-slate-400">(optional)</span>
                  </label>
                  <Input
                    data-testid="contact-phone-input"
                    value={form.contact_phone}
                    onChange={(e) => set("contact_phone", e.target.value)}
                    placeholder="Phone"
                    className="mt-1 rounded-none border-slate-200 bg-white py-6 focus-visible:ring-cardinal"
                  />
                </div>
              </div>

              <label
                data-testid="report-optin-label"
                className="flex cursor-pointer items-start gap-3 text-sm text-slate-600"
              >
                <Checkbox
                  data-testid="report-optin-checkbox"
                  checked={form.report_opt_in}
                  onCheckedChange={(v) => set("report_opt_in", !!v)}
                  className="mt-0.5 rounded-none border-slate-400 data-[state=checked]:border-cardinal data-[state=checked]:bg-cardinal"
                />
                <span>I would like to receive the annual California Rental Screening Report.</span>
              </label>

              <label
                data-testid="contact-optin-label"
                className="flex cursor-pointer items-start gap-3 text-sm text-slate-600"
              >
                <Checkbox
                  data-testid="contact-yes"
                  checked={form.contact_permission}
                  onCheckedChange={(v) => set("contact_permission", !!v)}
                  className="mt-0.5 rounded-none border-slate-400 data-[state=checked]:border-cardinal data-[state=checked]:bg-cardinal"
                />
                <span>You may contact me if additional research questions arise.</span>
              </label>
            </div>

            {/* consent */}
            <label
              data-testid="consent-label"
              className="flex cursor-pointer items-start gap-3 border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600"
            >
              <Checkbox
                data-testid="consent-checkbox"
                checked={form.consent}
                onCheckedChange={(v) => set("consent", !!v)}
                className="mt-0.5 rounded-none border-slate-400 data-[state=checked]:border-cardinal data-[state=checked]:bg-cardinal"
              />
              <span>
                I understand my experience may be summarized and categorized for housing
                research. My personal identity will not be published without my permission,
                and any names, addresses, or contact details I include will be automatically
                removed.
              </span>
            </label>
          </div>

          <div className="mt-10 flex items-center justify-between">
            <Button
              data-testid="step3-back"
              variant="ghost"
              onClick={() => setStep(2)}
              className="rounded-none text-slate-500 hover:text-ink"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button
              data-testid="submit-experience-button"
              disabled={submitting || !form.consent}
              onClick={submit}
              className="rounded-none bg-cardinal px-10 py-6 text-base font-medium text-white hover:bg-cardinal-hover"
            >
              {submitting ? "Submitting…" : "Add Your Voice"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
